// @LTG-ID: lib-external-cta
// @STATUS: BUILT @SHARED @EXTERNAL
// @HERMETIC: true
// @DOCS: 10_DECISIONS.md#ADR-005, 10_DECISIONS.md#ADR-008, 04b_SHARED_CONTRACT.md, 11_OPEN_QUESTIONS.md#OQ-001
// @CHANGED: 2026-02 session_002 (CHG-0029 fix)
// @OWNER: web
//
// <ExternalCTA> — canonical wrapper for every outbound app.letstalkgrowth.com link.
//
// Renders an <a> with href decorated by lib/url-utils.decorateAppLtgUrl, which
// merges UTM + click-ids from window.location.search and the currently-selected
// sector (via useSector). The same util is also used by useUtmPassthrough so the
// merge logic cannot drift between the two.
//
// Usage:
//   <ExternalCTA ctaId="hero-primary" className="ltg-btn ltg-btn-primary">
//     Begin  → app.letstalkgrowth.com/begin?via=hero  (CHG-0099)
//   </ExternalCTA>
//
//   <ExternalCTA ctaId="archetype-result-lifestyle" params={{ archetype: "lifestyle" }}>
//     ...
//   </ExternalCTA>
//
// Anti-pattern (will fail tester grep):
//   <a href="https://app.letstalkgrowth.com/...">…</a>

import { ctaHref, CTAS } from "./cta-ids";
import { useSector } from "./use-sector";
import { decorateAppLtgUrl } from "./url-utils";

export function ExternalCTA({
    ctaId,
    href,
    params,
    children,
    className,
    target = "_blank",
    rel = "noopener noreferrer",
    onClick,
    "data-testid": testId,
    ...rest
}) {
    const { sector } = useSector();
    const baseHref = href || ctaHref(ctaId);

    if (!baseHref) {
        if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.warn(
                `[ExternalCTA] ctaId "${ctaId}" has no path in cta-ids.js; rendering as a non-link span.`,
                CTAS[ctaId]
            );
        }
        return (
            <span
                className={className}
                data-cta-id={ctaId}
                data-testid={testId || `cta-${ctaId}`}
                {...rest}
            >
                {children}
            </span>
        );
    }

    const merged = { sector, ...(params || {}) };
    const decorated = decorateAppLtgUrl(baseHref, merged);

    return (
        <a
            className={className}
            href={decorated}
            target={target}
            rel={rel}
            data-cta-id={ctaId}
            data-testid={testId || `cta-${ctaId}`}
            onClick={onClick}
            {...rest}
        >
            {children}
        </a>
    );
}

export default ExternalCTA;
